import { ACTIVE_CATALOG } from "./market.js";
import type { Experience } from "./types.js";

export const CITY_EN: Record<string, string> = {
  北京: "Beijing",
  西安: "Xi'an",
  上海: "Shanghai",
  深圳: "Shenzhen",
  成都: "Chengdu",
  重庆: "Chongqing",
  三亚: "Sanya",
  桂林: "Guilin",
  阳朔: "Yangshuo",
  龙胜: "Longsheng",
  大理: "Dali",
  丽江: "Lijiang",
  张家界: "Zhangjiajie",
  华阴: "Mt. Huashan",
  "潮州/汕头": "Chaoshan",
  黔东南: "Qiandongnan",
  红河: "Honghe",
  香港: "Hong Kong",
  澳门: "Macau",
};
export const cityEn = (c: string): string => CITY_EN[c] || c;
export const CITY_TAG: Record<string, string> = {
  北京: "Imperial grandeur with hutong soul",
  西安: "Where China's story starts",
  上海: "The future, wearing a 1920s coat",
  深圳: "A fishing village that became a city in 40 years",
  成都: "The city that perfected taking it easy",
  重庆: "A cyberpunk city built on a mountain",
  三亚: "China's tropics, zero effort required",
  桂林: "The landscape on the money — literally",
  阳朔: "Karst peaks at cycling speed",
  龙胜: "Staircases of rice, carved by hand",
  大理: "Lake-and-mountain slow living",
  丽江: "Old town charm at 2,400 meters",
  张家界: "The mountains that inspired Avatar",
  华阴: "One sacred mountain, zero handrails",
  "潮州/汕头": "China's quiet food capital",
  黔东南: "Miao villages the tour buses haven't found",
  红河: "Terraced valleys at the edge of the map",
  香港: "Density, neon, and perfect wontons",
  澳门: "Egg tarts meet baroque churches",
};
export const CITY_INTRO: Record<string, string> = {
  北京: "Mornings belong to emperors — the Forbidden City's vermilion halls, the Wall snaking over ridgelines an hour north. But Beijing's real life is at street level: courtyard alleys where old men walk their songbirds, and lamb skewers appearing at dusk.",
  西安: "Two thousand years ago this was the largest city on earth, the eastern end of the Silk Road. The Terracotta Army gets the fame — stay for the Muslim Quarter at night: hand-pulled noodles, cumin smoke, and a 600-year-old wall you can cycle on top of.",
  上海: "Stand on the Bund and you get both centuries at once — 1920s Europe behind you, a sci-fi skyline across the river. Then the city goes small: plane-tree lanes, pan-fried buns at 7am, cocktail bars hidden behind bookshops.",
  深圳: "Forty years ago this was rice paddies and oyster beds. Now it is where much of the world's electronics are designed, and the city still does not quite believe its own skyline. The eastern coast is the part visitors miss — beaches, a Ming garrison town, and headlands that feel nothing like the CBD.",
  成都: "The pandas are real and twenty minutes away, but Chengdu's true export is pace — bamboo-chair teahouses, mahjong until dinner, and hotpot that arrives like a dare. Nobody leaves this city on schedule.",
  重庆: "A megacity stacked on mountains: trains run through apartment blocks, streets exist on eight levels, and the night skyline looks borrowed from a video game. Home to China's spiciest food — sweating is part of the view.",
  三亚: "China's Hawaii without crossing the Pacific. Warm water year-round, family-proof beaches, snorkeling islands offshore — the easiest days of any China trip.",
  桂林: "Those impossible mountains on the ¥20 note are real, and they line the Li River for fifty miles. Drift past on a bamboo raft at dawn, while the mist is still deciding what to be.",
  阳朔: "Guilin's landscape at handlebar height — e-bike lanes through rice paddies, karst peaks on every horizon, a river to drift down after lunch. The countryside China you didn't dare hope was real.",
  龙胜: "Seven hundred years of hand-carved rice terraces climbing entire mountains. Flooded in spring they turn to mirrors; sleep in a ridge village and watch them catch the sunrise.",
  大理: "An old kingdom's capital pinned between a forty-mile lake and a wall of mountains. Rent an e-bike, circle Erhai, stop wherever a village or a coffee shack looks right — Dali is a pace, not a checklist.",
  丽江: "Cobblestone canals, Naxi courtyards, and a 5,600-meter snow mountain hanging over the rooftops. Touristy at noon; pure magic after the day-trippers leave.",
  张家界:
    "Three thousand sandstone pillars floating in mist — the landscape Avatar borrowed. Glass bridges and cliff-face elevators, if you want your pulse involved.",
  华阴: "The most dramatic of China's five sacred mountains: granite faces, chain-assisted climbs, and the infamous plank walk bolted to a cliff. Pilgrims did this for a thousand years without the harness you'll be wearing.",
  "潮州/汕头":
    "The region Chinese food people whisper about: beef hotpot sliced to order, midnight congee streets, oolong poured gongfu-style between courses. Almost no Western tourists. Come hungry, leave converted.",
  黔东南:
    "Miao and Dong villages stacked up green valleys — silver headdresses at festivals, long-table feasts, a thousand households lighting one hillside at night. The China mass tourism hasn't found yet.",
  红河: "The Yuanyang terraces: whole valleys sculpted into flooded steps that catch fire at sunrise. Photographers plan years around this. You just need one clear winter morning.",
  香港: "Skyscrapers pressed against jungle hills, a harbour that performs nightly, wonton noodles worth the queue. Thirty minutes from Central you can be on a cliff trail above the sea.",
  澳门: "Portuguese plazas, baroque church facades, and the original egg tart — a 400-year cultural mashup you can walk end to end in an afternoon.",
};
export const cityChips = (city: string): string[] => {
  const es = ACTIVE_CATALOG.filter((c) => c.city === city);
  if (!es.length) return [];
  const mean = (f: (c: Experience) => number): number =>
    es.reduce((a: number, c: Experience) => a + f(c), 0) / es.length;
  const mx = (f: (c: Experience) => number): number => Math.max(...es.map(f));
  const cand: [number, string][] = [
    [mean((c) => -c.ax.terrain), "Nature-first"],
    [mean((c) => c.ax.terrain), "Big-city"],
    [mean((c) => -c.ax.era), "Ancient"],
    [mean((c) => c.ax.era), "Futuristic"],
    [mean((c) => -c.ax.energy), "Unhurried"],
    [mean((c) => c.ax.energy), "Electric"],
    [mean((c) => c.ax.fame) * 0.9, "Iconic"],
    [mean((c) => -c.ax.fame), "Under-the-radar"],
    [mean((c) => c.ax.lens), "Big vistas"],
    [mean((c) => -c.ax.lens), "Street-level"],
    [mx((c) => c.u.cul), "Food-obsessed"],
    [mx((c) => c.u.adv) * 0.85, "Adrenaline"],
    [mx((c) => c.u.cra) * 0.85, "Hands-on culture"],
    [mx((c) => c.u.nig) * 0.85, "Night-alive"],
  ];
  return cand
    .filter(([v]) => v > 0.4)
    .sort((a, b) => b[0] - a[0])
    .slice(0, 3)
    .map(([, l]) => l);
};

export const COORDS: Record<string, [number, number]> = {
  北京: [116.4, 39.9],
  西安: [108.9, 34.3],
  上海: [121.5, 31.2],
  成都: [104.1, 30.7],
  重庆: [106.5, 29.6],
  三亚: [109.5, 18.3],
  桂林: [110.3, 25.3],
  阳朔: [110.5, 24.7],
  龙胜: [110.0, 25.9],
  大理: [100.2, 25.6],
  丽江: [100.2, 26.9],
  张家界: [110.5, 29.1],
  华阴: [110.1, 34.6],
  "潮州/汕头": [116.6, 23.4],
  黔东南: [108.4, 26.6],
  红河: [102.8, 23.4],
  香港: [114.2, 22.3],
  澳门: [113.5, 22.2],
};
export const project = ([lon, lat]: [number, number]): [number, number] => [
  24 + ((lon - 96) / 27) * 252,
  20 + ((41.5 - lat) / 24) * 206,
];
